import { FV } from './share';
import { useTranslate } from '@tolgee/react';
import { DisabledProps, PlatformProps, RegionProps, ScenarioTypeProps } from '../TaskForm/share';
import { Button, Form, message } from 'antd';
import useCrud from 'hooks/useCrud';
import { ScenarioStore, scenarioStoreList } from 'views/Aide/service';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import useSmartField from 'hooks/useSmartField';
import PopoverPreview from 'components/StoreScopes/PopoverPreview';
import { Store, StoreScopesModal, getStoreCount } from 'components/StoreScopes';
import useModal from 'hooks/useModal';
import { Content } from '../TaskForm/StoreScopesField';
import { getAdminStores } from 'services/api/admin';
import { useStoreContext } from './StoreContext';

function StoresField({
  disabled: disabledProp,
  scenarioType,
  platform,
  region,
}: DisabledProps & ScenarioTypeProps & PlatformProps & RegionProps) {
  const { t } = useTranslate();

  const {
    values: { knowledgeId },
  } = useFormikContext<FV>();

  const { setStoreInfo } = useStoreContext();

  const disabled = disabledProp || Boolean(knowledgeId);

  const { item, fetch } = useCrud({
    read: scenarioStoreList,
  });

  const refsCurrent = {
    fetch,
    setStoreInfo,
    t,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    refs.current
      .fetch({
        scenarioType,
        platform,
        region,
      })
      .catch(() => {
        message.error(refs.current.t('marketing.获取店铺列表失败，请稍后再试'));
      });
  }, [scenarioType, platform, region]);

  const { storeMap, storeDataSource } = useStoresTransform(item?.stores);

  const externalStoreMap = useExternalStoreMap(platform, region);

  const {
    field: { value = [], onChange },
  } = useSmartField<FV['stores']>('stores');

  const storeScopes = useMemo(
    () => stores2storeScopes(value, platform, region, externalStoreMap),
    [value, platform, region, externalStoreMap],
  );

  useEffect(() => {
    refs.current.setStoreInfo((current) => ({
      ...current,
      storeScopes: getStoreCount(storeScopes) ? storeScopes : undefined,
    }));
  }, [storeScopes]);

  const { visible, show, hide } = useModal();

  const withFormItem = (...nodes: ReactNode[]) => (
    <Form.Item name="stores" label={t('aide.应用店铺')}>
      {...nodes}
    </Form.Item>
  );

  if (disabled) {
    return withFormItem(<PopoverPreview value={storeScopes} />);
  }

  const onStoreScopesChange = (storeScopes: General.StoreScope[]) =>
    onChange(storeScopes2Stores(storeScopes, item?.stores, storeMap));

  const withModal = (...nodes: ReactNode[]) =>
    withFormItem(
      <Content>
        <StoreScopesModal
          storeDataSource={storeDataSource}
          visible={visible}
          showFooterTips={false}
          onCancel={hide}
          value={storeScopes}
          onChange={onStoreScopesChange}
        />
        {...nodes}
      </Content>,
    );

  if (!value.length) {
    return withModal(
      <Button disabled={disabled} type="link" onClick={show}>
        {t('remind.选择店铺')}
      </Button>,
    );
  }

  return withModal(
    <PopoverPreview value={storeScopes} />,
    <Button disabled={disabled} type="link" onClick={show}>
      {t('phrase.重新选择')}
    </Button>,
  );
}

export function stores2storeScopes(
  stores: FV['stores'],
  platform: General.PlatformUpperCase,
  region: General.UpperCaseRegion,
  storeMap: Partial<Record<string, ScenarioStore>> = {},
): General.StoreScope[] {
  return [
    {
      platform,
      regionStoreScopes: [
        {
          region,
          storeIds: stores.reduce<number[]>((res, { storeId }) => {
            const { [storeId]: store } = storeMap;

            if (store) {
              res.push(store.internalId);
            }

            return res;
          }, []),
          storeCount: 0,
        },
      ],
      storeCount: 0,
    },
  ];
}

export function storeScopes2Stores(
  storeScopes: General.StoreScope[],
  storeList: ScenarioStore[] = [],
  storeMap: Partial<Record<number, ScenarioStore>> = {},
) {
  const storeTransfer = ({ externalId, name, alias }: ScenarioStore) => ({
    storeId: externalId,
    storeName: name,
    alias,
  });

  return storeScopes.reduce<FV['stores']>((res, cur) => {
    const { platform, regionStoreScopes } = cur;

    return regionStoreScopes.reduce((res, cur) => {
      const { region, storeIds = [], storeCount } = cur;

      if (region === 'all') {
        res.push(...storeList.filter((_) => _.externalType === platform).map(storeTransfer));

        return res;
      }

      if (!storeIds.length && storeCount) {
        res.push(
          ...storeList
            .filter((_) => _.externalType === platform && _.region === region)
            .map(storeTransfer),
        );

        return res;
      }

      return storeIds.reduce((res, storeId) => {
        const { [storeId]: store } = storeMap;

        if (store) {
          res.push(storeTransfer(store));
        }

        return res;
      }, res);
    }, res);
  }, []);
}

export function useStoresTransform(stores?: ScenarioStore[]) {
  return useMemo(() => {
    return (stores || []).reduce(
      (res, cur) => {
        const { internalId, externalId, externalType, region, name, alias } = cur;

        res.externalStoreMap[externalId] = cur;

        res.storeMap[internalId] = cur;

        res.storeDataSource.push({
          id: internalId,
          externalId,
          externalType,
          region,
          storeName: name,
          alias,
        });

        return res;
      },
      {
        externalStoreMap: {} as Partial<Record<string, ScenarioStore>>,
        storeMap: {} as Partial<Record<number, ScenarioStore>>,
        storeDataSource: [] as Store[],
      },
    );
  }, [stores]);
}

export function useExternalStoreMap(
  platform: General.PlatformUpperCase,
  region?: General.UpperCaseRegion,
) {
  const [externalStoreMap, setExternalStoreMap] = useState<Partial<Record<string, ScenarioStore>>>(
    {},
  );

  useEffect(() => {
    if (!region) {
      return;
    }

    getAdminStores({
      externalType: platform,
      region,
      page: 1,
      pageSize: 99999,
    })
      .then(
        ({
          data: {
            data: { stores },
          },
        }) => {
          const externalStoreMap = stores.reduce<Partial<Record<string, ScenarioStore>>>(
            (res, store) => {
              const { id, externalId, externalType, region, storeName, alias } = store;

              res[externalId] = {
                internalId: id,
                externalId,
                externalType,
                region,
                name: storeName,
                alias,
              };

              return res;
            },
            {},
          );

          setExternalStoreMap(externalStoreMap);
        },
      )
      .catch(() => {});
  }, [platform, region]);

  return externalStoreMap;
}

export default StoresField;
